import { useEffect, useState, useMemo } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography, Paper,
  TableContainer, MenuItem, Select, InputLabel, FormControl, CircularProgress,
  Checkbox,
} from '@mui/material';
import {
  Add, Edit, Delete, Autorenew as GerarIcon,
} from '@mui/icons-material';
import {
  getPagamentos, createPagamento, updatePagamento, deletePagamento,
  getContratosParaSelecao, getContratos,
} from '../services/api';

const computeReceitaStatus = ({ data_vencimento, data_pagamento }) => {
  const referenceDate = data_pagamento || data_vencimento;
  if (!referenceDate) return 'pendente';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parsedReference = new Date(referenceDate);
  if (Number.isNaN(parsedReference.getTime())) return 'pendente';
  parsedReference.setHours(0, 0, 0, 0);

  return parsedReference <= today ? 'pago' : 'pendente';
};

const emptyForm = {
  contrato_id: '',
  valor_devido: '',
  data_vencimento: '',
  data_pagamento: '',
};

const ReceitasPage = () => {
  const [receitas, setReceitas] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(() => new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const load = async () => {
    const [pagamentosData, contratosData] = await Promise.all([
      getPagamentos(),
      getContratosParaSelecao(),
    ]);
    const now = new Date();
    const currentMonth = now.getUTCMonth();
    const currentYear = now.getUTCFullYear();
    const isInCurrentMonth = (dateString) => {
      if (!dateString) return false;
      const parsed = new Date(dateString);
      if (Number.isNaN(parsed.getTime())) return false;
      return parsed.getUTCFullYear() === currentYear && parsed.getUTCMonth() === currentMonth;
    };

    const receitasDoMes = pagamentosData.filter((pagamento) => {
      const referenceDate = pagamento.data_pagamento || pagamento.data_vencimento;
      return isInCurrentMonth(referenceDate);
    });

    const receitasAtualizadas = receitasDoMes.map((pagamento) => ({
      ...pagamento,
      status: computeReceitaStatus(pagamento),
    }));
    setReceitas(receitasAtualizadas);
    setContratos(contratosData);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    setSelected((prev) => {
      const validIds = new Set(receitas.map((r) => r.id));
      const filtered = new Set([...prev].filter((id) => validIds.has(id)));
      if (filtered.size === prev.size) return prev;
      return filtered;
    });
  }, [receitas]);

  const contratoMap = new Map(contratos.map(c => [c.id, c.restaurantes?.nome || '']));

  const totalPrevisto = useMemo(() => {
    return receitas
      .filter(p => p.status === 'pendente')
      .reduce((acc, p) => acc + Number(p.valor_devido || 0), 0);
  }, [receitas]);

  const totalRecebido = useMemo(() => {
    return receitas
      .filter(p => p.status === 'pago')
      .reduce((acc, p) => acc + Number(p.valor_devido || 0), 0);
  }, [receitas]);

  const handleGerarPrevisao = async () => {
    if (!window.confirm('Deseja gerar as receitas previstas para o mês atual? Lançamentos já existentes serão mantidos.')) return;

    setIsLoading(true);
    try {
      const [pagamentosData, contratosRecentes] = await Promise.all([
        getPagamentos(),
        getContratos(), // Ajustado para buscar os contratos completos
      ]);

      const now = new Date();
      const currentMonth = now.getUTCMonth(); // 0-11
      const currentYear = now.getUTCFullYear();
      const pad = (value) => String(value).padStart(2, '0');
      const diasNoMes = new Date(Date.UTC(currentYear, currentMonth + 1, 0)).getUTCDate();
      
      const normalizarData = (value) => {
        if (!value) return null;
        const [date] = value.split('T');
        return date;
      };

      const pagamentosDoMes = pagamentosData.reduce((acc, pagamento) => {
        const date = normalizarData(pagamento.data_vencimento || pagamento.data_pagamento);
        if (!date) return acc;
        const [year, month] = date.split('-').map(Number);
        if (year === currentYear && month === currentMonth + 1) {
          acc.add(`${pagamento.contrato_id}-${date}`);
        }
        return acc;
      }, new Set());

      const primeiroDiaMes = new Date(Date.UTC(currentYear, currentMonth, 1));
      const ultimoDiaMes = new Date(Date.UTC(currentYear, currentMonth, diasNoMes, 23, 59, 59, 999));

      const contratosAtivosNoPeriodo = contratosRecentes.filter((contrato) => {
        const status = (contrato?.status || '').toLowerCase();
        if (status !== 'ativo') return false;

        const dataInicio = contrato?.data_inicio ? new Date(contrato.data_inicio) : null;
        if (dataInicio && dataInicio > ultimoDiaMes) return false;

        const dataFim = contrato?.data_fim ? new Date(contrato.data_fim) : null;
        if (dataFim && dataFim < primeiroDiaMes) return false;

        return true;
      });

      let verificados = 0;
      let criados = 0;
      let ignorados = 0;

      for (const contrato of contratosAtivosNoPeriodo) {
        const diaReferencia = Number(contrato?.dia_vencimento || contrato?.dia_pagamento);
        if (!diaReferencia || Number.isNaN(diaReferencia)) continue;

        verificados += 1;

        const diaSeguro = Math.min(Math.max(diaReferencia, 1), diasNoMes);
        const dataVencimento = `${currentYear}-${pad(currentMonth + 1)}-${pad(diaSeguro)}`;
        const chave = `${contrato.id}-${dataVencimento}`;

        if (pagamentosDoMes.has(chave)) {
          ignorados += 1;
          continue;
        }

        const valorDevido = contrato?.valor_mensal ?? contrato?.valor ?? contrato?.valor_devido ?? '';

        const payload = {
          contrato_id: contrato.id,
          valor_devido: valorDevido,
          data_vencimento: dataVencimento,
          data_pagamento: null,
          status: 'pendente',
        };

        await createPagamento(payload);
        pagamentosDoMes.add(chave);
        criados += 1;
      }

      alert(`Processo concluído!\n- Contratos avaliados: ${verificados}\n- Novas receitas criadas: ${criados}\n- Receitas já existentes: ${ignorados}`);
      
      await load(); 
    } catch (error) {
      console.error('Erro ao gerar previsão:', error);
      alert('Ocorreu um erro ao gerar a previsão. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = (receita = null) => {
    setEditing(receita);
    setForm(
      receita
        ? {
            contrato_id: receita.contrato_id || '',
            valor_devido: receita.valor_devido || '',
            data_pagamento: receita.data_pagamento ? receita.data_pagamento.split('T')[0] : '',
            data_vencimento: receita.data_vencimento ? receita.data_vencimento.split('T')[0] : '',
          }
        : emptyForm
    );
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSave = async () => {
    const payload = {
      ...form,
      contrato_id: form.contrato_id || null,
      valor_devido: form.valor_devido || '',
      data_vencimento: form.data_vencimento || null,
      data_pagamento: form.data_pagamento || null,
      status: computeReceitaStatus(form),
    };
    if (editing) await updatePagamento(editing.id, payload);
    else await createPagamento(payload);
    handleClose();
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir receita?')) return;
    await deletePagamento(id);
    setSelected((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    load();
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Excluir ${selected.size} receita${selected.size === 1 ? '' : 's'} selecionada${selected.size === 1 ? '' : 's'}?`)) return;
    setIsBulkDeleting(true);
    try {
      await Promise.all([...selected].map((id) => deletePagamento(id)));
      setSelected(new Set());
      load();
    } catch (error) {
      alert('Não foi possível excluir as receitas selecionadas.');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectGroup = (items) => {
    const groupAllSelected = items.length > 0 && items.every((item) => selected.has(item.id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (groupAllSelected) {
        items.forEach((item) => next.delete(item.id));
      } else {
        items.forEach((item) => next.add(item.id));
      }
      return next;
    });
  };

  const allSelected = receitas.length > 0 && receitas.every((item) => selected.has(item.id));
  const someSelected = receitas.some((item) => selected.has(item.id));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Receitas do Mês Atual</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleDeleteSelected}
            disabled={selected.size === 0 || isBulkDeleting}
          >
            {isBulkDeleting ? 'Excluindo...' : 'Excluir Selecionadas'}
          </Button>
          <Button
            variant="outlined"
            startIcon={isLoading ? <CircularProgress size={20} /> : <GerarIcon />}
            onClick={handleGerarPrevisao}
            disabled={isLoading}
          >
            {isLoading ? 'Gerando...' : 'Gerar Previsão'}
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>Nova Receita</Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">Total Previsto (Pendente + Atrasado)</Typography>
          <Typography variant="h5" fontWeight={700} color="primary.main">
            {totalPrevisto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">Total Recebido (Pago)</Typography>
          <Typography variant="h5" fontWeight={700} color="success.main">
            {totalRecebido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </Typography>
        </Paper>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={() => handleToggleSelectGroup(receitas)}
                />
              </TableCell>
              <TableCell>Contrato (Restaurante)</TableCell>
              <TableCell>Valor Devido</TableCell>
              <TableCell>Vencimento</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {receitas.map((p) => (
              <TableRow key={p.id} hover sx={{ opacity: p.status === 'pago' ? 0.6 : 1 }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.has(p.id)}
                    onChange={() => toggleSelect(p.id)}
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  Contrato #{p.contrato_id} ({contratoMap.get(p.contrato_id) || '...'})
                </TableCell>
                <TableCell>{Number(p.valor_devido || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                <TableCell>{new Date(p.data_vencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</TableCell>
                <TableCell>{p.status}</TableCell>
                <TableCell align="right">
                  <IconButton color="primary" onClick={() => handleOpen(p)} aria-label="Editar receita"><Edit /></IconButton>
                  <IconButton color="error" onClick={() => handleDelete(p.id)} aria-label="Excluir receita"><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {receitas.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center">Nenhuma receita cadastrada.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Editar Receita' : 'Nova Receita'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Contrato</InputLabel>
            <Select
              name="contrato_id"
              label="Contrato"
              value={form.contrato_id}
              onChange={handleChange}
            >
              {contratos.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  Contrato #{c.id} ({c.restaurantes?.nome || 'Sem restaurante'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField label="Valor Devido" name="valor_devido" value={form.valor_devido} onChange={handleChange} fullWidth />
          <TextField label="Data Vencimento" type="date" name="data_vencimento" value={form.data_vencimento} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Data Pagamento" type="date" name="data_pagamento" value={form.data_pagamento} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceitasPage;